// Turn ANY Zunou message body into honest plain text — never raw JSON.
//
// Shape spec assembled from a live prod census (iFLYER Ops, 2026-07-06) plus a
// full code sweep of the dashboard renderer (MessageContentUI / MessageListV2 /
// FormattedContent), the Nova renderer, the API writers (Services/Agents,
// meeting summary jobs, topic system messages) and the shared Slate editor:
//
//  - Slate JSON arrays. Element types the editors can produce: paragraph,
//    bulleted-list / numbered-list / list-item, heading(-one/two/three),
//    block-quote, code / code-block, link {url}, mention {mention:{id,name}},
//    task-pill {task:{title}}, note-pill {note:{title}}, event-pill
//    {event:{title}}, object-link {objectTitle, objectType} (Nova's generic
//    pill — sometimes a message is ONLY one of these), emoji nodes; leaf marks
//    (bold/italic/…) carry no text of their own.
//  - System/AI objects {message, ui:{type}} with ui.type ∈ references |
//    options | confirmation | new_topic | event_draft | event_edit — the
//    readable parts live in message + ui.references[].title /
//    ui.options[].label / ui.confirmation.prompt+actions / ui.event.name+date.
//  - AI content objects {type:'meeting_list'|'survey_questionnaire'|
//    'review_tasks'|'summary_options', data:{meetings|questions|topic…}}.
//  - Task-insight action payloads: [{label, action}] or {Group:[{label,…}]}.
//  - Digest bundles [{name, created_at, content, gravatar}].
//  - Attachment payloads {content:[{name|file_name…}]}.
//  - HTML strings (<p>…</p>) from older editors / email ingest.
//  - Markdown strings (meeting summaries post markdown straight into chat —
//    the dashboard pipes them through react-markdown, so syntax must not leak).
//  - Plain strings.

const NAMED_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
}

function decodeEntities(s: string): string {
  return s.replace(/&(#x?[0-9a-fA-F]+|\w+);/g, (whole, code: string) => {
    if (NAMED_ENTITIES[code]) return NAMED_ENTITIES[code]
    if (code.startsWith('#')) {
      const n = code.startsWith('#x') || code.startsWith('#X')
        ? Number.parseInt(code.slice(2), 16)
        : Number.parseInt(code.slice(1), 10)
      if (Number.isFinite(n) && n > 0) {
        try {
          return String.fromCodePoint(n)
        } catch {
          return whole
        }
      }
    }
    return whole
  })
}

function stripHtml(s: string): string {
  return decodeEntities(
    s
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/(p|div|li|tr|h[1-6]|blockquote)>/gi, '\n')
      .replace(/<[^>]+>/g, ''),
  )
}

const looksHtml = (s: string): boolean => /<[a-z][^>]*>/i.test(s)

const looksMarkdown = (s: string): boolean =>
  /(\*\*|__|^#{1,6}\s|\[[^\]]+\]\([^)]+\)|^```|^[-*]\s)/m.test(s)

/** Flatten markdown to reading text — the dashboard renders these bodies via
 *  react-markdown, so literal asterisks/hashes must never reach the user. */
function stripMarkdown(s: string): string {
  return s
    .replace(/^```[^\n]*$/gm, '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    .replace(/(^|\s)\*([^*\s][^*]*)\*(?=\s|[.,!?;:]|$)/g, '$1$2')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1')
    .replace(/^\s*[-*]\s+/gm, '• ')
}

const cleanup = (s: string): string =>
  s
    .replace(/[ \t]+/g, ' ')
    .replace(/ ?\n ?/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()

// Slate node types that end a line when flattened.
const BLOCKS = new Set([
  'paragraph',
  'heading',
  'heading-one',
  'heading-two',
  'heading-three',
  'block-quote',
  'list-item',
  'code',
  'code-block',
])

const PILL_GLYPH: Record<string, string> = {
  task: '☑',
  note: '📄',
  event: '🗓',
}

function pillText(kind: string, title: unknown): string {
  const t = typeof title === 'string' && title.trim() ? title.trim() : kind
  const glyph = PILL_GLYPH[kind]
  return glyph ? `${glyph} ${t}` : t
}

/** Flatten Slate nodes (or any children/text tree) to text. */
function nodeText(node: unknown): string {
  if (node == null) return ''
  if (Array.isArray(node)) return node.map(nodeText).join('')
  if (typeof node === 'string') return node
  if (typeof node !== 'object') return ''
  const o = node as Record<string, unknown>
  if (typeof o.text === 'string') return o.text
  // Mentions carry their display name OUTSIDE the (empty) text children.
  const mention = o.mention as Record<string, unknown> | undefined
  if (mention && typeof mention.name === 'string') return `@${mention.name}`
  // Inline object pills: the title lives on the node, children are empty —
  // a message can be NOTHING BUT one of these, so they must produce text.
  if (o.type === 'object-link') {
    return pillText(String(o.objectType ?? ''), o.objectTitle)
  }
  if (o.type === 'task-pill') {
    return pillText('task', (o.task as Record<string, unknown>)?.title)
  }
  if (o.type === 'note-pill') {
    return pillText('note', (o.note as Record<string, unknown>)?.title)
  }
  if (o.type === 'event-pill') {
    return pillText('event', (o.event as Record<string, unknown>)?.title)
  }
  if (o.type === 'emoji') {
    const e = o.emoji ?? o.character ?? o.unicode
    if (typeof e === 'string') return e
  }
  let inner = Array.isArray(o.children) ? nodeText(o.children) : ''
  if (o.type === 'link' && !inner.trim() && typeof o.url === 'string') {
    inner = o.url
  }
  if (typeof o.type === 'string' && BLOCKS.has(o.type)) inner += '\n'
  return inner
}

// Slate-ish means it actually HAS node content — a bare {type:'meeting_list'}
// AI payload is not Slate even though it has a `type` string.
const isSlateish = (o: Record<string, unknown>): boolean =>
  'children' in o || 'text' in o

function firstStringField(
  o: Record<string, unknown>,
  keys: string[],
): string | null {
  for (const k of keys) {
    const v = o[k]
    if (typeof v === 'string' && v.trim()) return v
  }
  return null
}

const asArray = (v: unknown): Array<Record<string, unknown>> =>
  Array.isArray(v)
    ? (v.filter((x) => x && typeof x === 'object') as Array<
        Record<string, unknown>
      >)
    : []

/** Labels from [{label,…}] arrays (AI option menus, task-insight actions). */
function labelList(v: unknown): string[] {
  return asArray(v)
    .map((x) => (typeof x.label === 'string' ? x.label.trim() : ''))
    .filter(Boolean)
}

/** The readable extras hiding inside a ui payload, beyond `message`. */
function uiExtras(ui: Record<string, unknown>): string {
  const parts: string[] = []
  const refs = asArray(ui.references)
    .map((r) => (typeof r.title === 'string' ? r.title.trim() : ''))
    .filter(Boolean)
  if (refs.length > 0) parts.push(`→ ${refs.join(', ')}`)
  const options = labelList(ui.options)
  if (options.length > 0) parts.push(`[${options.join(' · ')}]`)
  const confirmation = ui.confirmation as Record<string, unknown> | undefined
  if (confirmation) {
    const prompt =
      typeof confirmation.prompt === 'string' ? confirmation.prompt.trim() : ''
    const actions = labelList(confirmation.actions)
    const line = [prompt, actions.length ? `[${actions.join(' / ')}]` : '']
      .filter(Boolean)
      .join(' ')
    if (line) parts.push(line)
  }
  const event = ui.event as Record<string, unknown> | undefined
  if (event) {
    const name = typeof event.name === 'string' ? event.name.trim() : ''
    const when = [event.date, event.start_at, event.end_at]
      .filter((x): x is string => typeof x === 'string' && Boolean(x.trim()))
      .join(' ')
    const line = ['→', name, when].filter(Boolean).join(' ')
    if (name || when) parts.push(line)
  }
  return parts.join('\n')
}

/** AI content payloads keyed by data: meeting lists, surveys. */
function dataExtras(o: Record<string, unknown>): string {
  const data = o.data as Record<string, unknown> | undefined
  if (!data) return ''
  const meetings = asArray(data.meetings)
    .map((m) => {
      const title = firstStringField(m, ['title', 'name'])
      const bits = [m.date, m.organizer_name]
        .filter((x): x is string => typeof x === 'string' && Boolean(x.trim()))
        .join(', ')
      if (!title) return ''
      return bits ? `${title} (${bits})` : title
    })
    .filter(Boolean)
  if (meetings.length > 0) return `Meetings: ${meetings.join(', ')}`
  const topic = typeof data.topic === 'string' ? data.topic.trim() : ''
  const questions = asArray(data.questions)
    .map((q) => (typeof q.question === 'string' ? q.question.trim() : ''))
    .filter(Boolean)
  if (topic || questions.length > 0) {
    const qs = questions.slice(0, 3).join(' · ')
    return ['Survey:', topic, topic && qs ? '—' : '', qs]
      .filter(Boolean)
      .join(' ')
  }
  return ''
}

function fromObject(o: Record<string, unknown>, depth: number): string {
  // System/AI messages: the human line lives in `message` (dashboard renders
  // exactly this plus an interactive widget), enriched with the widget's own
  // readable parts (reference titles, option labels, confirmation prompt…).
  const direct = firstStringField(o, [
    'message',
    'summary',
    'title',
    'prompt',
    'description',
    'prompt_description',
  ])
  const ui = o.ui as Record<string, unknown> | undefined
  const extras = [ui ? uiExtras(ui) : '', dataExtras(o)]
    .filter(Boolean)
    .join('\n')
  if (direct) {
    const head = looksHtml(direct) ? stripHtml(direct) : direct
    return extras ? `${head}\n${extras}` : head
  }
  if (extras) return extras
  if (typeof o.content === 'string') {
    const nested = extract(o.content, depth + 1)
    if (nested) return nested
  }
  if (Array.isArray(o.content)) {
    // Either nested rich nodes or an attachments array.
    const t = nodeText(o.content)
    if (t.trim()) return t
    const names = (o.content as Array<Record<string, unknown> | null>)
      .map((a) => a && firstStringField(a, ['name', 'file_name', 'filename']))
      .filter((n): n is string => Boolean(n))
    if (names.length > 0) return `📎 ${names.join(', ')}`
    if (o.content.length > 0) return `📎 ${o.content.length} attachment(s)`
  }
  if (isSlateish(o)) {
    const t = nodeText(o)
    if (t.trim()) return t
  }
  // Grouped action menus: {"Task Insights": [{label, action}], …}
  const grouped = Object.values(o)
    .flatMap((v) => labelList(v))
    .filter(Boolean)
  if (grouped.length > 0) return grouped.join(' · ')
  // Nothing readable — describe the widget instead of dumping JSON.
  if (ui && typeof ui.type === 'string') {
    return `[${ui.type.replace(/_/g, ' ')}]`
  }
  return ''
}

function extract(raw: string, depth: number): string {
  if (depth > 3) return ''
  const t = raw.trim()
  if (!t) return ''
  if (t.startsWith('{') || t.startsWith('[') || t.startsWith('"')) {
    try {
      const j: unknown = JSON.parse(t)
      if (typeof j === 'string') return extract(j, depth + 1) || j
      if (typeof j === 'number' || typeof j === 'boolean') return String(j)
      if (Array.isArray(j)) {
        const first = j.find((x) => x != null)
        if (first && typeof first === 'object') {
          const fo = first as Record<string, unknown>
          if (
            !isSlateish(fo) &&
            ('content' in fo || 'message' in fo)
          ) {
            // A digest bundle of message-like objects → "Name: text" lines.
            const lines = (j as Array<Record<string, unknown>>)
              .slice(0, 6)
              .map((item) => {
                const body =
                  typeof item.content === 'string'
                    ? extract(item.content, depth + 1)
                    : fromObject(item, depth + 1)
                const name =
                  typeof item.name === 'string' ? item.name.trim() : ''
                if (name && body) return `${name}: ${body}`
                return body || name
              })
              .filter(Boolean)
            if (lines.length > 0) return cleanup(lines.join('\n'))
          }
          if (!isSlateish(fo) && typeof fo.label === 'string') {
            // Action menus: [{label, action}, …] → the labels.
            const labels = labelList(j)
            if (labels.length > 0) return labels.join(' · ')
          }
        }
        return cleanup(nodeText(j))
      }
      if (j && typeof j === 'object') {
        return cleanup(fromObject(j as Record<string, unknown>, depth))
      }
      return ''
    } catch {
      // Not valid JSON after all — fall through to the string paths.
    }
  }
  if (looksHtml(t)) return cleanup(stripHtml(raw))
  if (looksMarkdown(t)) return cleanup(stripMarkdown(raw))
  return raw
}

/**
 * Human-readable plain text for any message content. Returns '' when the
 * payload has no readable text at all (never raw JSON).
 */
export function messageText(raw: string | null | undefined): string {
  if (!raw) return ''
  return extract(String(raw), 0)
}
